import { DestinationTypeLabels } from "../../../models/destinationType.map";
import { deliveryDateKey } from "./suiviLivraisonFormat";

// Règles du suivi : recherche paginée des patients et des livraisons, regroupements par patient, commande, flux et
// jour, actions réalisées et commentaires affichés pour chaque produit.

export const DEFAULT_PATIENT_TRACKING_STATUSES = [
    "TRAITE",
    "EN_COMMANDE",
    "EN_RUPTURE",
    "EN_LIVRAISON",
    "LIVRE",
];

export const SUIVI_PAGE_SIZE = 20;
export const TRAITEMENTS_PAGE_SIZE = 5;
export const LIVRAISONS_PAGE_SIZE = 20;

export function patientKey(row) {
    return row.patientId ?? `${row.patientNom || ""}:${row.patientPrenom || ""}`;
}

function groupBy(items, keyOf) {
    const map = new Map();
    items.forEach(item => {
        const key = keyOf(item);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(item);
    });
    return map;
}

export function groupRowsByPatient(rows) {
    return [...groupBy(rows, patientKey).values()];
}

export function buildSuiviParams(filters, page) {
    const { dateReception, ehpadId, tournee, patientSearch, produitSearch, statutMode } = filters;
    const params = new URLSearchParams();
    if (dateReception) params.append("dateReception", dateReception);
    if (ehpadId) params.append("ehpadId", ehpadId);
    if (tournee) params.append("tournee", tournee);
    if (patientSearch) params.append("patient", patientSearch);
    if (produitSearch) params.append("produit", produitSearch);
    if (statutMode === "TOUS") {
        DEFAULT_PATIENT_TRACKING_STATUSES.forEach(statut => params.append("statuts", statut));
    } else {
        params.append("statuts", statutMode);
    }
    params.append("page", String(page));
    params.append("size", String(SUIVI_PAGE_SIZE));
    return params;
}

// Le serveur répond par une page ou, à défaut, par la liste brute des lignes.
export function suiviPageFromResponse(responseData) {
    const data = responseData || {};
    const content = data.content ?? data;
    return {
        rows: Array.isArray(content) ? content : [],
        totalPages: data.totalPages ?? (content.length > 0 ? 1 : 0),
        totalElements: data.totalElements ?? content.length,
    };
}

export function traitementsPageFromResponse(responseData, nextPage) {
    const data = responseData || {};
    const content = Array.isArray(data) ? data : data.content || [];
    return {
        content,
        page: data.number ?? nextPage,
        totalPages: data.totalPages ?? (content.length > 0 ? nextPage + 1 : nextPage),
    };
}

export function hasNextPage(page, totalPages) {
    return totalPages > 0 && page < totalPages - 1;
}

export function isNearScrollEnd(target, threshold) {
    return target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
}

export function ehpadSummary(row) {
    return [row.ehpadNom || row.ehpadCode, row.ehpadTournee].filter(Boolean).join(" · ") || "EHPAD non renseigné";
}

export function groupTraitementsByCommande(rows) {
    return [...groupBy(rows, row => row.commandePatientId ?? row.commandeId ?? row.id).values()];
}

const DESTINATION_ORDER = ["ROBOT", "ETIQUETAGE"];

function compareDestinations(a, b) {
    const ia = DESTINATION_ORDER.indexOf(a);
    const ib = DESTINATION_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
}

export function groupCommandeRowsByDestination(rows) {
    return [...groupBy(rows || [], row => row.destination || "AUTRE").entries()]
        .sort(([a], [b]) => compareDestinations(a, b));
}

export function canRetraiter(row) {
    return row.statut === "LIVRE" || row.statut === "EN_LIVRAISON";
}

// Un produit renvoyé en production perd son statut et sa validation de mise en caisse.
export function retraitedTraitement(traitement) {
    return { ...traitement, statut: null, productionValidated: false };
}

export function actionTimeline(row) {
    return [
        {
            label: "Facturation",
            at: row.billingHour,
            by: row.billingUsername,
            active: row.billingValidated || row.billingHour || row.billingUsername,
        },
        {
            label: "Planification",
            at: row.planificationValidatedAt,
            by: row.planificationValidatedBy,
            active: row.planificationValidated || row.planificationValidatedAt || row.planificationValidatedBy,
        },
        {
            label: "Mise en caisse",
            at: row.caisseHour,
            by: row.caisseUsername,
            active: row.productionValidated || row.caisseHour || row.caisseUsername,
        },
    ].filter(action => action.active);
}

export function productionComments(row) {
    return [
        row?.commentaryProductionRobot ? ["Robot", row.commentaryProductionRobot] : null,
        row?.commentaryProductionEtiquetage ? ["Étiquetage", row.commentaryProductionEtiquetage] : null,
    ].filter(Boolean);
}

function destinationLabel(destination) {
    if (destination === "ETIQUETAGE") return "Étiquetage";
    if (destination === "ROBOT") return "Robot";
    return DestinationTypeLabels[destination] || destination || "Flux";
}

// Commentaires de planification d'un flux : chaque texte n'est affiché qu'une fois.
export function destinationComments(destination, rows) {
    const field = destination === "ETIQUETAGE" ? "commentaryPlanificationEtiquetage" : "commentaryPlanificationRobot";
    const comments = [];
    const seen = new Set();
    (rows || []).forEach(row => {
        const value = row?.[field];
        if (!value || seen.has(value)) return;
        seen.add(value);
        comments.push(value);
    });
    return { label: destinationLabel(destination), comments };
}

export function buildLivraisonsParams({ ehpadId, dateLivraison }, page) {
    const params = new URLSearchParams();
    if (ehpadId) params.append("ehpadId", ehpadId);
    if (dateLivraison) {
        params.append("dateDebut", dateLivraison);
        params.append("dateFin", dateLivraison);
    }
    params.append("page", String(page));
    params.append("size", String(LIVRAISONS_PAGE_SIZE));
    params.append("includeMedicaments", "false");
    return params;
}

export function livraisonsPageFromResponse(responseData) {
    const data = responseData || {};
    return {
        content: data.content || [],
        totalPages: data.totalPages || 0,
        totalElements: data.totalElements || 0,
    };
}

export function groupLivraisonsByDate(livraisons) {
    const sorted = [...(livraisons || [])]
        .sort((a, b) => new Date(b.dateLivraison || 0) - new Date(a.dateLivraison || 0));
    return [...groupBy(sorted, livraison => deliveryDateKey(livraison.dateLivraison)).entries()]
        .map(([key, items]) => ({ key, date: items[0]?.dateLivraison, items }));
}

export function caissesLabel(livraison) {
    const count = livraison.nombreCaisses;
    return count ? `${count} caisse${count > 1 ? "s" : ""}` : "Caisses non renseignées";
}

export function photosLabel(livraison) {
    if (!livraison.signedBonAvailable) return "";
    const count = livraison.signedBonPhotoCount || 1;
    return ` · ${count} photo${count > 1 ? "s" : ""}`;
}

export function totalUnites(medicaments) {
    return medicaments.reduce((sum, med) => sum + (med.quantite || 0), 0);
}
